<template>
  <div>
    <vue-good-table
      class="mt-1"
      theme="polar-bear"
      mode="remote"
      :columns="columns"
      :rows="records"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        perPage: serverParams.perPage,
        position: 'bottom',
        perPageDropdown: [5, 10, 20, 50, 100],
        dropdownAllowAll: false,
        setCurrentPage: 1,
        nextLabel: 'next',
        prevLabel: 'prev',
        rowsPerPageLabel: 'Rows per page',
        ofLabel: 'of',
        pageLabel: 'page',
        allLabel: 'All',
      }"
      :search-options="{
        enabled: false,
      }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true,
      }"
      :sort-options="{
        enabled: true,
      }"
      @on-page-change="onPageChange"
      @on-sort-change="onSortChange"
      @on-column-filter="onColumnFilter"
      @on-per-page-change="onPerPageChange"
      @on-selected-rows-change="onSelectionChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Position Column -->
        <span v-if="props.column.field === 'position'">
          <div class="d-flex flex-column">
            <span class="font-weight-bold">{{ props.row.position }}</span>
            <small class="text-muted">{{ props.row.company }}</small>
          </div>
        </span>

        <!-- Date Posted Column -->
        <span v-else-if="['created_on'].includes(props.column.field)">
          {{ $formatDate(props.row[props.column.field]) }}
        </span>

        <!-- Actions Column -->
        <span v-else-if="props.column.field === 'actions'">
          <div class="d-flex align-items-center">
            <b-button
              :variant="props.row.is_favorite ? 'flat-warning' : 'flat-secondary'"
              size="sm"
              class="btn-icon mr-50"
              @click="toggleFavorite(props.row)"
            >
              <feather-icon :icon="props.row.is_favorite ? 'StarIcon' : 'StarIcon'" />
            </b-button>
          </div>
        </span>

        <!-- Column: match_rate -->
        <span v-else-if="props.column.field === 'match_rate'">
          <b-row class="mx-auto">
            <b-badge class="mx-auto">{{ (props.row.match_rate.total * 100).toFixed(1) }}%</b-badge>
            <div
              class="w-100 d-flex flex-row align-items-center"
              @mouseenter="showHover(props.column.field, props.row[props.column.field], true, $event)"
              @mouseleave="showHover(props.column.field, props.row[props.column.field], false, $event)"
              @mousemove="handleHoverMouseMove($event)"
            >

              <b-progress
                class="w-75 mt-1 w-100"
                max="100"
              >
                <b-progress-bar
                  :value="props.row.match_rate.total * 100"
                  :label="`${(props.row.match_rate.total * 100).toFixed(1)}%`"
                  :style="`background-color: ${$getProgressColor(props.row.match_rate.total * 100)};`"
                />
              </b-progress>
            </div>

          </b-row>
        </span>

        <!-- Default Column -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
    </vue-good-table>

    <template v-if="['match_rate'].includes(hoverProperties.currentHover)">
      <div
        v-if="
          hoverProperties[hoverProperties.currentHover].state
            && $isNotEmpty(hoverProperties[hoverProperties.currentHover].data.details)
        "
        v-show="
          hoverProperties[hoverProperties.currentHover].state
        "
        class="mouse-follow-div"
        :style="floatingDivStyle"
      >
        <template v-for="[key, value] in Object.entries(hoverProperties[hoverProperties.currentHover].data.details)">
          <div class="d-flex flex-row flex-gap-md align-items-center justify-content-between" :key="key.key">
            <div
              :style="`width: 15ch;`"
            >
              {{ $capitalizeString(key) }}
            </div>

            <b-progress
              :style="`width: 15ch;`"
              :max="hoverProperties[hoverProperties.currentHover].data.weight[key] * 100"
            >
              <b-progress-bar
                :value="value.score * 100"
                :style="`background-color: ${
                  $getProgressColor(
                    value.score * 100,
                    hoverProperties[hoverProperties.currentHover].data.weight[key] * 100
                  )};`"
              />
            </b-progress>

            <div
              :style="`width: 14ch; text-align: end;`"
            >
              {{ (value.score * 100).toFixed(2) }}
              /
              {{ (hoverProperties[hoverProperties.currentHover].data.weight[key] * 100).toFixed(2) }}
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import { hoverDisplayMixins } from "@/mixins/hoverDisplayMixins"

export default {
  mixins: [hoverDisplayMixins], 
  components: {
    VueGoodTable,
  },
  props: {
    id: {
      type: String,
      default: null,
    },

    refresh: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isLoading: false,
      records: [],

      columns: [
        {
          label: 'Candidate Name',
          field: 'candidate_name',
          filterOptions: {
            enabled: false,
            trigger: 'enter',
          },
          sortable: false,
        },
        {
          label: 'Match Rate',
          field: 'match_rate',
          filterOptions: {
            enabled: false,
            trigger: 'enter',
          },
          sortable: false,
        },
        {
          label: 'Date Matched',
          field: 'created_on',
          sortable: true,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
          filterOptions: {
            enabled: false,
            trigger: 'enter',
          },
        },
      ],

      totalRecords: 0,
      serverParams: {
        columnFilters: {},
        sort: {},
        page: 1,
        perPage: 10,
      },
    }
  },

  mounted() {
    this.getRecords()
  },

  methods: {
    toggleFavorite(row) {
      this.$http
        .post(
          `/api/broker/matches/${row.id}/save`
        )
        .then(response => {
          const { success, output } = response.data

          if (success) {
            this.getRecords() 
          }
        })
        .catch(error => {
          //
        })

    },

    getRecords() {
      this.isLoading = true

      this.$http
        .get(
          `/api/broker/jobs/${this.id}/matches?refresh=${this.refresh}`
        )
        .then(response => {
          const { success, output } = response.data

          this.isLoading = false
          if (success) {
            this.records = output
          }
        })
        .catch(error => {
          this.isLoading = false
        })

    },

    onPageChange(params) {
      this.serverParams.page = params.currentPage
      this.getRecords()
    },

    onSortChange(params) {
      this.serverParams.sort = {
        field: params[0].field,
        type: params[0].type,
      }
      this.getRecords()
    },

    onColumnFilter(params) {
      this.serverParams.columnFilters = params.columnFilters
      this.getRecords()
    },

    onPerPageChange(params) {
      this.serverParams.perPage = params.currentPerPage
      this.getRecords()
    },

    onSelectionChange(params) {
      // console.log('Selected rows:', params.selectedRows)
    },
  }
}
</script>

<style>

</style>